import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { Filter, Calendar, Search } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { getUserReadings } from '../../lib/supabase';
import Card, { CardContent } from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import { trackEvent, trackException } from '../../lib/analytics';

const ReadingHistory: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [readings, setReadings] = useState<any[]>([]);
  const [filteredReadings, setFilteredReadings] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedType, setSelectedType] = useState<string | null>(null);
  
  useEffect(() => {
    // Track page visit
    trackEvent('reading_history', 'page_visit', 'history_page');
  }, []);

  useEffect(() => {
    const fetchReadings = async () => {
      if (!user) {
        navigate('/auth');
        return;
      }
      
      try {
        const userReadings = await getUserReadings(user.phoneNumber);
        setReadings(userReadings);
        setFilteredReadings(userReadings);
        
        trackEvent('reading_history', 'loaded', userReadings.length.toString());
      } catch (err: any) {
        console.error('Error fetching readings:', err);
        setError('Failed to load your reading history. Please try again.');
        
        trackEvent('reading_history', 'error', 'fetch_failed');
        trackException(`Reading history fetch failed: ${err.message}`, false);
      } finally {
        setLoading(false);
      }
    };
    
    fetchReadings();
  }, [user, navigate]);
  
  useEffect(() => {
    // Apply filters and search
    let filtered = [...readings];
    
    if (selectedType) {
      filtered = filtered.filter(reading => reading.reading_type === selectedType);
      trackEvent('reading_history', 'filter_applied', selectedType);
    }
    
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(reading => 
        reading.reading_type.toLowerCase().includes(query) || 
        reading.prediction_text.toLowerCase().includes(query)
      );
      trackEvent('reading_history', 'search_applied', searchQuery);
    }
    
    setFilteredReadings(filtered);
  }, [readings, selectedType, searchQuery]);
  
  const handleReadingClick = (readingId: string, readingType: string) => {
    trackEvent('reading_history', 'reading_clicked', readingType);
    navigate(`/reading/result/${readingId}`);
  };
  
  const readingTypes = ['daily', 'weekly', 'monthly', 'yearly', 'career', 'love', 'health'];
  
  const formatReadingDate = (dateString: string) => {
    return format(new Date(dateString), 'MMM d, yyyy');
  };
  
  return (
    <div className="min-h-screen bg-neutral-50 py-8 px-4">
      <div className="max-w-5xl mx-auto">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-primary-800 font-display">Your Reading History</h1>
          <p className="mt-2 text-neutral-600">
            Browse and access all your previous astrological readings
          </p>
        </div>
        
        {/* Filters and Search */}
        <Card className="mb-6">
          <CardContent className="py-4">
            <div className="flex flex-col md:flex-row md:items-center gap-4">
              <div className="flex-grow">
                <Input
                  placeholder="Search readings..."
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  leftIcon={<Search size={18} />}
                  fullWidth
                />
              </div>
              
              <div className="flex items-center space-x-2 overflow-x-auto pb-2 md:pb-0">
                <span className="text-sm text-neutral-500 flex items-center">
                  <Filter size={16} className="mr-1" /> Filter:
                </span>
                <Button
                  size="sm"
                  variant={selectedType === null ? 'primary' : 'ghost'}
                  onClick={() => {
                    setSelectedType(null);
                    trackEvent('reading_history', 'filter_clear', 'all');
                  }}
                >
                  All
                </Button>
                {readingTypes.map(type => (
                  <Button
                    key={type}
                    size="sm"
                    variant={selectedType === type ? 'primary' : 'ghost'}
                    onClick={() => setSelectedType(type)}
                  >
                    {type.charAt(0).toUpperCase() + type.slice(1)}
                  </Button>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>
        
        {/* Readings List */}
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-600"></div>
          </div>
        ) : error ? (
          <div className="bg-error-50 text-error-600 p-4 rounded-lg">
            <p>{error}</p>
            <Button 
              onClick={() => {
                trackEvent('reading_history', 'retry', 'error_retry');
                window.location.reload();
              }} 
              variant="outline" 
              className="mt-2"
              size="sm"
            >
              Try Again
            </Button>
          </div>
        ) : filteredReadings.length > 0 ? (
          <div className="space-y-4">
            {filteredReadings.map(reading => (
              <Card 
                key={reading.reading_id}
                className="hover:shadow-md cursor-pointer transition-shadow"
                onClick={() => handleReadingClick(reading.reading_id, reading.reading_type)}
              >
                <CardContent className="p-0">
                  <div className="flex flex-col md:flex-row">
                    <div className={`w-full md:w-1/4 p-4 ${
                      reading.reading_type === 'daily' ? 'bg-amber-50' :
                      reading.reading_type === 'weekly' ? 'bg-blue-50' :
                      reading.reading_type === 'monthly' ? 'bg-violet-50' :
                      reading.reading_type === 'yearly' ? 'bg-purple-50' :
                      reading.reading_type === 'career' ? 'bg-green-50' :
                      reading.reading_type === 'love' ? 'bg-red-50' :
                      'bg-teal-50'
                    }`}>
                      <h3 className="font-semibold capitalize">
                        {reading.reading_type} Reading
                      </h3>
                      <div className="flex items-center text-sm text-neutral-500 mt-1">
                        <Calendar size={14} className="mr-1" />
                        {formatReadingDate(reading.created_at)}
                      </div>
                    </div>
                    
                    <div className="w-full md:w-3/4 p-4">
                      <p className="text-neutral-700 line-clamp-3">
                        {reading.prediction_text.substring(0, 200)}...
                      </p>
                      <div className="text-right mt-2">
                        <span className="text-primary-600 text-sm font-medium">
                          View Full Reading →
                        </span>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-neutral-100 mb-4">
              <Calendar className="h-8 w-8 text-neutral-500" />
            </div>
            <h2 className="text-xl font-semibold mb-2">No Readings Found</h2>
            <p className="text-neutral-600 mb-6">
              {searchQuery || selectedType 
                ? "No readings match your search criteria. Try different filters."
                : "You haven't had any readings yet. Get your first astrological reading now!"}
            </p>
            {!searchQuery && !selectedType && (
              <Button onClick={() => {
                trackEvent('reading_history', 'get_first_reading', 'empty_state');
                navigate('/');
              }}>
                Get Your First Reading
              </Button>
            )}
            {(searchQuery || selectedType) && (
              <Button 
                variant="outline"
                onClick={() => {
                  setSearchQuery('');
                  setSelectedType(null);
                  trackEvent('reading_history', 'clear_filters', 'no_results');
                }}
              >
                Clear Filters
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ReadingHistory;